/**
 * Route copy for `src/routes/advocacy` (keys: `routes.advocacy.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  advocacyLayout: {
    myFeed: 'My feed',
    leaderboard: 'Leaderboard',
    campaignsAdmin: 'Campaigns (admin)',
    ambassadorsEmployeeAdvocacy: 'Ambassadors (Employee advocacy)',
    shareTheBrandSContentFrom: 'Share the brand\'s content from your own account. No automatic sending: you decide, with your consent, and every link is tracked (UTM).',
  },
  advocateFeedPage: {
    readOnlyCannotShare: '🔒 Read-only — your role can browse the feed but cannot join the program or share.',
    couldNotLoadTheFeed: 'Could not load the feed.',
    shareRecordedOpenTheLinkTo: 'Share recorded — open the link to post on your account.',
    shareRefusedReason: 'Share refused ({{reason}}).',
    theShareFailed: 'The share failed.',
    consentRecordedThankYou: 'Consent recorded — thank you!',
    couldNotRecordTheConsent: 'Could not record the consent.',
    theAmbassadorProgramIsNotAvailable: 'The ambassador program is not available on this environment yet.',
    youArePartOfTheProgram: '✓ You are part of the program',
    consentFrom: 'consent from',
    youHaveLeftTheProgram: 'You have left the program.',
    actionNotPossible: 'Action not possible.',
    leave: 'Leave',
    youHaveLeftTheProgramA: 'You have left the program. A new share will ask for your agreement again.',
    noShareIsPostedOnYour: 'No share is posted on your behalf. On the first share, we will ask for your consent (CASL / Law 25).',
    loading: 'Loading…',
    noActiveCampaignRightNowCome: 'No active campaign right now. Come back later — or ask your manager to activate a campaign.',
    yourConsent: 'Your consent',
    cancel: 'Cancel',
    iAcceptAndShare: 'I accept and share',
    iAgreeToTakePartIn: 'I agree to take part in the ambassador program and for my shares to be tracked (UTM link + referral identifier) to measure the campaign\'s impact.',
    nothingIsPostedAutomaticallyOnYour: 'Nothing is posted automatically on your accounts: you post yourself.',
    youCanLeaveAtAnyTime: 'You can leave at any time (“Leave”).',
    yourConsentIsTimestampedAndArchived: 'Your consent is timestamped and archived (CASL / Law 25).',
    couldNotCopySelectTheText: 'Could not copy — select the text manually.',
    clickNotRecorded: 'Click not recorded.',
    suggestedTextEditable: 'Suggested text (editable)',
    noSuggestedTextWriteYourOwn: 'No suggested text — write your own.',
    trackedLink: 'Tracked link:',
    thisCampaignHasNoDestinationUrl: 'This campaign has no destination URL.',
    platform: 'Platform',
    share: 'Share',
    shareRecordedClicksTrackedClickS: '✓ Share recorded · {{clicks}} tracked click(s)',
    openSharePlatformLabel: 'Open {{SHARE_PLATFORM_LABEL}}',
    copyTextLink: 'Copy text + link',
    testTheLinkCountsAClick: 'Test the link (counts a click)',
    textAndLinkCopied: 'Text and link copied.',
  },
  campaignsAdminPage: {
    draft: 'Draft',
    active: 'Active',
    paused: 'Paused',
    archived: 'Archived',
    campaignUpdated: 'Campaign updated.',
    campaignCreatedDraft: 'Campaign created (draft).',
    savingFailed: 'Saving failed.',
    textGeneratedProofreadItBeforeActivating: 'Text generated — proofread it before activating.',
    aiIsNotAvailableRightNow: 'AI is not available right now.',
    textBlockedByTheComplianceFilter: 'Text blocked by the compliance filter.',
    couldNotGenerate: 'Could not generate.',
    editTheCampaign: 'Edit the campaign',
    newCampaignDraft: 'New campaign (draft)',
    name: 'Name',
    description: 'Description',
    destinationUrl: 'Destination URL',
    utmSource: 'utm_source',
    utmMedium: 'utm_medium',
    utmCampaign: 'utm_campaign',
    suggestedShareText: 'Suggested share text',
    writeWithAi: '✨ Write with AI',
    consentRequiredBeforeAnyShareRecommended: 'Consent required before any share (recommended — CASL)',
    cancel: 'Cancel',
    save: 'Save',
    createTheDraft: 'Create the draft',
    measurementUnavailable: 'Measurement unavailable.',
    loading: 'Loading…',
    clickShareRate: 'Click/share rate',
    sharesShareSClicksClickS: ': {{shares}} share(s) / {{clicks}} click(s)',
    couldNotLoadTheCampaigns: 'Could not load the campaigns.',
    couldNotChangeTheStatus: 'Could not change the status.',
    couldNotArchive: 'Could not archive.',
    ambassadorInvitedTheyMustAcceptTo: 'Ambassador invited — they must accept to take part.',
    theInvitationFailedValidEmail: 'The invitation failed (valid email?).',
    theAmbassadorProgramIsNotAvailable: 'The ambassador program is not available on this environment yet.',
    ambassadorCampaigns: 'Ambassador campaigns',
    createContentToAmplifyDraftFirst: 'Create content to amplify (draft first), activate it, then measure shares and clicks.',
    newCampaign: 'New campaign',
    noCampaignsCreateOneToOffer: 'No campaigns. Create one to offer a content feed to your ambassadors.',
    consentRequired: 'Consent required',
    hideTheMeasurement: 'Hide the measurement',
    measure: 'Measure',
    edit: 'Edit',
    enable: 'Enable',
    pause: 'Pause',
    archive: 'Archive',
    ambassadorsLength: 'Ambassadors ({{length}})',
    email: 'Email',
    invite: 'Invite',
    noAmbassadorInvitedYet: 'No ambassador invited yet.',
    ambassador: 'Ambassador',
    status: 'Status',
    consent: 'Consent',
    withdrawn: 'Withdrawn',
    guest: 'Guest',
    active2: 'Active',
    shares: 'Shares',
    clicks: 'Clicks',
    firstnameCompanyCom: 'firstname@company.com',
  },
  leaderboardPage: {
    couldNotLoadTheLeaderboard: 'Could not load the leaderboard.',
    theAmbassadorProgramIsNotAvailable: 'The ambassador program is not available on this environment yet.',
    trackedClicks: 'Tracked clicks',
    engagedAmbassadors: 'Engaged ambassadors',
    ambassadorsOptInOnly: 'Ambassadors (opt-in only)',
    loading: 'Loading…',
    theAmbassadorLeaderboardIsNotAvailable: 'The ambassador leaderboard is not available on this environment yet.',
    noConsentingAmbassadorHasSharedYet: 'No consenting ambassador has shared yet. Only those who gave their consent appear here.',
    ambassador: 'Ambassador',
    you: '(you)',
    shareSClicksClickS: 'share(s) · {{clicks}} click(s)',
    leaderboardOfConsentingAmbassadorsComput: 'Leaderboard of consenting ambassadors, computed on the server from tracked shares (UTM + ref). The campaign totals below are complete as well.',
    topPerformingCampaigns: 'Top-performing campaigns',
    noActiveOrPausedCampaign: 'No active or paused campaign.',
    sharesSharedShareSClicksTotal: '{{shares_shared}} share(s) · {{clicks_total}} click(s) · {{advocates_engaged}} ambassador(s)',
    measurementUnavailable: 'Measurement unavailable',
    sharesClicksClickS: ': {{shares}} / {{clicks}} click(s)',
    shares: 'Shares',
  },
  useMyAdvocate: {
    couldNotLoad: 'Could not load.',
    yourProfileHasNoEmail: 'Your profile has no email.',
  },
};

export default en;
